import type { ReactNode } from "react"
import { agentAvatarColor } from "@/lib/tone"
import { cn } from "@/lib/utils"
import { type Presence, TONES, type Tone } from "@/model/view-types"

/** Presence uses the glyph colours: online/working live (green), waiting/away hollow grey. */
const presenceDot: Record<Presence, string> = {
  online: "bg-live",
  working: "bg-live",
  waiting: "border-[1.5px] border-wait bg-surface",
  away: "border-[1.5px] border-wait bg-surface",
}

/**
 * People are circles, agents squares (refined-system §5.9), both on `fill` with ink initials. `solid` is Lea's ink
 * monogram (a serif initial on ink). An agent's square takes her chosen colour (`agentAvatarColor`, white text; UX pass
 * 2026-10-15); a person's circle ignores the tone.
 */
export function Avatar({
  label,
  tone,
  shape = "square",
  size = 28,
  solid = false,
  presence,
  className,
  title,
}: {
  label: string
  tone?: Tone | undefined
  shape?: "square" | "circle" | undefined
  size?: 20 | 22 | 24 | 28 | 30 | 32 | 34 | undefined
  solid?: boolean | undefined
  presence?: Presence | undefined
  className?: string | undefined
  title?: string | undefined
}) {
  const color = shape === "square" && tone ? agentAvatarColor[tone] : null
  return (
    <span
      title={title}
      data-avatar-color={color ?? undefined}
      className={cn(
        "relative inline-flex shrink-0 items-center justify-center select-none",
        shape === "circle" ? "rounded-full" : "rounded-control",
        solid
          ? cn("monogram", color ? "text-white" : "bg-ink text-paper")
          : cn(
              color ? "text-white" : "bg-fill text-ink",
              size <= 22 ? "avatar-initials" : "text-[12px] font-semibold",
            ),
        className,
      )}
      style={{
        width: size,
        height: size,
        ...(color ? { backgroundColor: color } : {}),
        ...(solid && size > 22 ? { fontSize: Math.round(size * 0.55) } : {}),
      }}
    >
      {label}
      {presence && (
        <span
          className={cn(
            "absolute -right-0.5 -bottom-0.5 size-2 rounded-full ring-2 ring-surface",
            presenceDot[presence],
          )}
        />
      )}
    </span>
  )
}

export function AvatarStack({
  children,
  className,
}: {
  children: ReactNode
  className?: string | undefined
}) {
  return <span className={cn("flex [&>*]:ring-2 [&>*]:ring-paper [&>*+*]:-ml-1", className)}>{children}</span>
}

/**
 * An agent's avatar from her record: her emoji or initial, her colour (`avatar.color`), Lea's monogram for the lead.
 * The one way to draw an agent where her record is at hand.
 */
export function AgentAvatar({
  agent,
  lead = false,
  size = 28,
  presence,
  className,
  title,
}: {
  agent: { readonly name: string; readonly avatar: { readonly emoji: string | null; readonly color: string } }
  lead?: boolean | undefined
  size?: 20 | 22 | 24 | 28 | 30 | 32 | 34 | undefined
  presence?: Presence | undefined
  className?: string | undefined
  title?: string | undefined
}) {
  return (
    <Avatar
      label={agent.avatar.emoji || (agent.name[0] ?? "?").toUpperCase()}
      tone={toneOfColor(agent.avatar.color)}
      solid={lead}
      size={size}
      presence={presence}
      className={className}
      title={title}
    />
  )
}

/** A stored colour as a tone ("slate" and unknown → neutral, the old "red" → amber), like `model/view` `toneOf`. */
const toneOfColor = (color: string): Tone =>
  TONES.find((t) => t === color) ?? (color === "red" ? "amber" : "neutral")
